import { useEffect, useState } from "react";
import { Link, NavLink, Navigate, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Trash, SignOut, Plus, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

export function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();

  if (loading) return <div className="p-8 text-[#555555]">Chargement…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-[#F8F9FA]">
      <aside className="md:w-64 bg-[#0D2C54] text-white md:min-h-screen p-6 md:p-8">
        <div className="flex items-center gap-2 mb-10">
          <div className="w-9 h-9 bg-[#E63946] flex items-center justify-center"><span className="font-display font-bold">M</span></div>
          <div>
            <div className="font-display font-bold">MECA AUTO 17</div>
            <div className="overline text-[10px] text-white/60">Admin</div>
          </div>
        </div>
        <nav className="space-y-1">
          {[
            { to: "/admin", label: "Rendez-vous", end: true },
            { to: "/admin/devis", label: "Devis" },
            { to: "/admin/articles", label: "Articles" },
          ].map((i) => (
            <NavLink
              key={i.to}
              to={i.to}
              end={i.end}
              data-testid={`admin-nav-${i.label.toLowerCase()}`}
              className={({ isActive }) =>
                `block px-4 py-3 text-sm transition-colors ${isActive ? "bg-[#E63946] text-white" : "text-white/80 hover:bg-white/10"}`
              }
            >
              {i.label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-10 pt-6 border-t border-white/10 space-y-3">
          <div className="text-xs text-white/60 overline">Connecté</div>
          <div className="text-sm">{user.email}</div>
          <Button data-testid="admin-logout" onClick={async () => { await logout(); nav("/admin/login"); }} variant="outline" className="w-full bg-transparent border-white/30 text-white hover:bg-white hover:text-[#0D2C54] rounded-none h-10">
            <SignOut size={16} weight="bold" className="mr-2" /> Déconnexion
          </Button>
          <Link to="/" className="block text-xs text-white/60 hover:text-white text-center pt-2">← Retour au site</Link>
        </div>
      </aside>
      <main className="flex-1 p-6 md:p-10 max-w-full overflow-x-auto">
        <Outlet />
      </main>
    </div>
  );
}

export function AdminAppointments() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/admin/appointments").then((r) => setItems(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    await api.patch(`/admin/appointments/${id}`, { status });
    toast.success("Statut mis à jour");
    load();
  };
  const remove = async (id) => {
    if (!confirm("Supprimer ce rendez-vous ?")) return;
    await api.delete(`/admin/appointments/${id}`);
    toast.success("Supprimé");
    load();
  };

  return (
    <div data-testid="admin-appointments">
      <div className="flex items-center justify-between mb-8">
        <div>
          <span className="overline text-[#E63946]">Admin</span>
          <h1 className="font-display font-bold text-3xl text-[#0D2C54] mt-1">Rendez-vous</h1>
        </div>
        <Badge variant="outline" className="rounded-none">{items.length} demande{items.length > 1 ? "s" : ""}</Badge>
      </div>
      {loading ? <p>Chargement…</p> : (
        <div className="bg-white border border-[#E5E7EB] overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead><TableHead>Client</TableHead><TableHead>Contact</TableHead><TableHead>Véhicule</TableHead><TableHead>Service</TableHead><TableHead>Souhaité</TableHead><TableHead>Statut</TableHead><TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((a) => (
                <TableRow key={a.id} data-testid={`admin-appt-row-${a.id}`}>
                  <TableCell className="text-xs">{new Date(a.created_at).toLocaleDateString("fr-FR")}</TableCell>
                  <TableCell className="font-medium">{a.prenom} {a.nom}</TableCell>
                  <TableCell className="text-xs">{a.telephone}<br />{a.email}</TableCell>
                  <TableCell className="text-xs">{a.vehicule || "-"}<br />{a.immatriculation}</TableCell>
                  <TableCell className="text-xs">{a.service}</TableCell>
                  <TableCell className="text-xs">{a.date_souhaitee}</TableCell>
                  <TableCell>
                    <Select defaultValue={a.status} onValueChange={(v) => updateStatus(a.id, v)}>
                      <SelectTrigger className="h-8 w-32 text-xs rounded-none"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="nouveau">Nouveau</SelectItem>
                        <SelectItem value="confirme">Confirmé</SelectItem>
                        <SelectItem value="termine">Terminé</SelectItem>
                        <SelectItem value="annule">Annulé</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <button data-testid={`admin-appt-delete-${a.id}`} onClick={() => remove(a.id)} className="text-[#E63946] hover:bg-[#E63946] hover:text-white p-2"><Trash size={16} /></button>
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-[#555555] py-8">Aucune demande.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

export function AdminQuotes() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/quotes").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="admin-quotes">
      <div className="flex items-center justify-between mb-8">
        <div>
          <span className="overline text-[#E63946]">Admin</span>
          <h1 className="font-display font-bold text-3xl text-[#0D2C54] mt-1">Devis</h1>
        </div>
        <Badge variant="outline" className="rounded-none">{items.length} demande{items.length > 1 ? "s" : ""}</Badge>
      </div>
      <div className="bg-white border border-[#E5E7EB] overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow><TableHead>Date</TableHead><TableHead>Client</TableHead><TableHead>Véhicule</TableHead><TableHead>Service</TableHead><TableHead>Description</TableHead><TableHead></TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {items.map((q) => (
              <TableRow key={q.id} data-testid={`admin-quote-row-${q.id}`}>
                <TableCell className="text-xs">{new Date(q.created_at).toLocaleDateString("fr-FR")}</TableCell>
                <TableCell className="text-xs"><div className="font-medium">{q.prenom} {q.nom}</div>{q.email}<br />{q.telephone}</TableCell>
                <TableCell className="text-xs">{q.vehicule}</TableCell>
                <TableCell className="text-xs">{q.service}</TableCell>
                <TableCell className="text-xs max-w-md whitespace-normal">{q.description}</TableCell>
                <TableCell>
                  <button data-testid={`admin-quote-delete-${q.id}`} onClick={async () => { if (confirm("Supprimer ?")) { await api.delete(`/admin/quotes/${q.id}`); load(); } }} className="text-[#E63946] p-2"><Trash size={16} /></button>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-[#555555] py-8">Aucune demande.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export function AdminArticles() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", excerpt: "", content: "", image_url: "", category: "Conseils" });

  const load = () => api.get("/articles").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.title || !form.excerpt || !form.content) {
      toast.error("Titre, extrait et contenu requis");
      return;
    }
    try {
      if (editing) await api.patch(`/admin/articles/${editing.id}`, form);
      else await api.post("/admin/articles", form);
      toast.success("Enregistré");
      setEditing(null);
      setForm({ title: "", excerpt: "", content: "", image_url: "", category: "Conseils" });
      load();
    } catch (e) {
      toast.error("Erreur d'enregistrement");
    }
  };

  const edit = (a) => {
    setEditing(a);
    setForm({ title: a.title, excerpt: a.excerpt, content: a.content, image_url: a.image_url || "", category: a.category });
  };

  const remove = async (id) => {
    if (!confirm("Supprimer cet article ?")) return;
    await api.delete(`/admin/articles/${id}`);
    toast.success("Article supprimé");
    load();
  };

  return (
    <div data-testid="admin-articles">
      <div className="flex items-center justify-between mb-8">
        <div>
          <span className="overline text-[#E63946]">Admin</span>
          <h1 className="font-display font-bold text-3xl text-[#0D2C54] mt-1">Articles</h1>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-5 bg-white border border-[#E5E7EB] p-6">
          <h2 className="font-display font-semibold text-lg text-[#0D2C54] mb-4">{editing ? "Modifier l'article" : "Nouvel article"}</h2>
          <div className="space-y-4">
            <div><Label className="overline text-xs">Titre</Label><Input data-testid="article-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-none" /></div>
            <div><Label className="overline text-xs">Catégorie</Label><Input data-testid="article-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-none" /></div>
            <div><Label className="overline text-xs">URL Image</Label><Input data-testid="article-image" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="rounded-none" placeholder="https://..." /></div>
            <div><Label className="overline text-xs">Extrait</Label><Textarea data-testid="article-excerpt" rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className="rounded-none resize-none" /></div>
            <div><Label className="overline text-xs">Contenu</Label><Textarea data-testid="article-content" rows={8} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="rounded-none resize-none" /></div>
            <div className="flex gap-2">
              <Button data-testid="article-save" onClick={save} className="bg-[#E63946] hover:bg-[#D62839] rounded-none flex-1"><Plus size={16} className="mr-2" />{editing ? "Mettre à jour" : "Créer"}</Button>
              {editing && <Button variant="outline" onClick={() => { setEditing(null); setForm({ title: "", excerpt: "", content: "", image_url: "", category: "Conseils" }); }} className="rounded-none">Annuler</Button>}
            </div>
          </div>
        </div>

        <div className="lg:col-span-7 bg-white border border-[#E5E7EB] divide-y divide-[#E5E7EB]">
          {items.map((a) => (
            <div key={a.id} data-testid={`admin-article-${a.id}`} className="p-4 flex items-center justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="overline text-xs text-[#E63946]">{a.category}</div>
                <div className="font-medium text-[#0D2C54] truncate">{a.title}</div>
                <div className="text-xs text-[#555555] truncate">{a.excerpt}</div>
              </div>
              <div className="flex gap-1">
                <button data-testid={`admin-article-edit-${a.id}`} onClick={() => edit(a)} className="p-2 text-[#0D2C54] hover:bg-[#F8F9FA]"><PencilSimple size={16} /></button>
                <button data-testid={`admin-article-delete-${a.id}`} onClick={() => remove(a.id)} className="p-2 text-[#E63946] hover:bg-[#F8F9FA]"><Trash size={16} /></button>
              </div>
            </div>
          ))}
          {items.length === 0 && <div className="p-8 text-center text-[#555555]">Aucun article.</div>}
        </div>
      </div>
    </div>
  );
}
